/*
 * Copyright (C) Ivo Ladage-van Doorn - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Ivo Ladage-van Doorn <ivol37@gmail.com>, 2024
 */
import { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListIcon from "@mui/icons-material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { mainMenuItems, MenuItem } from "./Menu";
import { theme } from "../assets/styles/theme";
import React from "react";

const drawerWidth = 240;

export const scrollToFootnote = (index?: number) => {
  const element = document.getElementById("footnote-" + index);
  const yOffset = -100;
  if (element) {
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

function Navigation({}: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState<string>(location.pathname);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        const scrolled = window.scrollY > navbar.clientHeight;
        setScrolled(scrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const openPage = (newPage: string) => {
    navigate(newPage);
    setPage(newPage);
  };

  const isSelected = (item: MenuItem): boolean => {
    return page.startsWith(item.url);
  };

  const isSelectedSub = (item: MenuItem, subitem: MenuItem): boolean => {
    return page === item.url + subitem.url;
  };

  const pageToName = () => {
    const mainMenu = mainMenuItems.filter((item) => item.url === "/" + page.split("/")[1])[0];
    if (!mainMenu) return "Lekkveld / Zomervakanties";
    if (!mainMenu.submenu) return mainMenu.name;
    const subMenu = mainMenu.submenu?.filter((item) => item.url === "/" + page.split("/")[2])[0];
    return mainMenu.name + " / " + subMenu?.name;
  };

  const drawer = (
    <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <p className="mobile-menu-top">
        <ListIcon />
        Menu
      </p>
      <Divider />
      <List>
        {mainMenuItems.map((item) => (
          <React.Fragment key={item.url}>
            <ListItem key={item.url} disablePadding sx={{ backgroundColor: isSelected(item) ? theme.palette.primary.dark : "" }}>
              <ListItemButton
                sx={{ color: isSelected(item) ? "white" : "" }}
                onClick={() => openPage(item.url + (item.submenu ? item.submenu[0].url : ""))}
                selected={isSelected(item)}
              >
                <ListItemText primary={item.name} />
              </ListItemButton>
            </ListItem>
            {isSelected(item) &&
              item.submenu?.map((subitem) => (
                <ListItem key={subitem.url} disablePadding sx={{ backgroundColor: isSelectedSub(item, subitem) ? theme.palette.primary.light : "" }}>
                  <ListItemButton
                    sx={{ marginLeft: "25px", color: isSelectedSub(item, subitem) ? "white" : "" }}
                    onClick={() => openPage(item.url + subitem.url)}
                    selected={isSelectedSub(item, subitem)}
                  >
                    <ListItemText primary={subitem.name} />
                  </ListItemButton>
                </ListItem>
              ))}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", height: "64px" }}>
        <CssBaseline />
        <AppBar sx={{ backgroundColor: "primary.dark" }} component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? " scrolled" : ""}`}>
          <Toolbar className="navigation-bar">
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: "none" } }}>
              <MenuIcon />
            </IconButton>
            {/* {mode === "dark" ? <LightModeIcon onClick={() => modeChange()} /> : <DarkModeIcon onClick={() => modeChange()} />} */}
            <p>{pageToName()}</p>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              {mainMenuItems.map((item) => (
                <Button
                  className="navigation"
                  key={item.url}
                  onClick={() => openPage(item.url + (item.submenu ? item.submenu[0].url : ""))}
                  sx={{ color: "#fff" }}
                  variant={page.indexOf(item.url) !== -1 || (item.url === "/lekkveld" && page === "/") ? "contained" : "text"}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          </Toolbar>
        </AppBar>
        <nav>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, backgroundColor: "#2B352C", color: "#D3C6B5" },
            }}
          >
            {drawer}
          </Drawer>
        </nav>
      </Box>
    </ThemeProvider>
  );
}

export default Navigation;
